// Système audio pour HAL 3000
// Sons disponibles dans /public/sounds/

class AudioManager {
  constructor() {
    this.sounds = {};
    this.audioContext = null;
    this.initialized = false;
  }

  init() {
    if (this.initialized) return;
    
    // Créer le contexte audio au premier clic utilisateur
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.initialized = true;
    } catch (e) {
      console.warn('AudioContext not available');
    }
  }

  // Jouer un son Red Alert en boucle
  playRedAlert(loops = 3) {
    return this.playSound('/sounds/red-alert.mp3', { volume: 0.7, loops });
  }

  // Bip de confirmation
  playBeep() {
    return this.playGeneratedBeep(800, 100);
  }

  // Bip d'erreur
  playError() {
    return this.playGeneratedBeep(300, 200, 'sawtooth');
  }

  // Bip de succès
  playSuccess() {
    return this.playGeneratedBeep(1000, 150, 'sine');
  }

  // Son d'accès refusé
  playAccessDenied() {
    this.playGeneratedBeep(200, 100, 'square');
    setTimeout(() => this.playGeneratedBeep(150, 200, 'square'), 150);
  }

  // Son de boot/startup
  playStartup() {
    const notes = [400, 500, 600, 800];
    notes.forEach((freq, i) => {
      setTimeout(() => this.playGeneratedBeep(freq, 100, 'sine', 0.2), i * 120);
    });
  }

  // Son de notification
  playNotification() {
    this.playGeneratedBeep(880, 80, 'sine', 0.3);
    setTimeout(() => this.playGeneratedBeep(1100, 80, 'sine', 0.3), 100);
  }

  // Jouer un fichier audio
  async playSound(url, options = {}) {
    const { volume = 0.5, loops = 1 } = options;
    
    try {
      const audio = new Audio(url);
      audio.volume = volume;
      
      let playCount = 0;
      audio.onended = () => {
        playCount++;
        if (playCount < loops) {
          audio.currentTime = 0;
          audio.play();
        }
      };
      
      await audio.play();
      return audio;
    } catch (e) {
      console.warn('Cannot play sound:', e);
      return null;
    }
  }

  // Générer un bip synthétique
  playGeneratedBeep(frequency = 800, duration = 100, type = 'square', volume = 0.3) {
    this.init();
    
    if (!this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = type;
      gainNode.gain.value = volume;

      oscillator.start();
      
      // Fade out
      gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration / 1000);
      
      setTimeout(() => {
        oscillator.stop();
      }, duration + 50);
    } catch (e) {
      console.warn('Cannot generate beep:', e);
    }
  }

  // Séquence d'alerte complète (Red Alert style)
  async playAlertSequence() {
    // Essayer de jouer le fichier Red Alert
    const audio = await this.playSound('/sounds/red-alert.mp3', { volume: 0.6, loops: 2 });
    
    // Fallback sur les bips générés si le fichier ne charge pas
    if (!audio) {
      for (let i = 0; i < 3; i++) {
        setTimeout(() => {
          this.playGeneratedBeep(600, 300, 'square', 0.4);
          setTimeout(() => this.playGeneratedBeep(400, 300, 'square', 0.4), 350);
        }, i * 800);
      }
    }
    
    return audio;
  }
}

// Instance singleton
const audioManager = new AudioManager();

// Initialiser au premier clic
if (typeof window !== 'undefined') {
  const initOnInteraction = () => {
    audioManager.init();
    window.removeEventListener('click', initOnInteraction);
    window.removeEventListener('touchstart', initOnInteraction);
  };
  window.addEventListener('click', initOnInteraction);
  window.addEventListener('touchstart', initOnInteraction);
}

export default audioManager;
export { AudioManager };
